import { apiClient, unwrap } from "../../../api/client";
import type { Device } from "../queries";
import { parseLabel } from "./label";
import { base64ToBytes, bytesToBase64, computeClaimMac } from "./proof";

export type ClaimInput = {
  label: string;
  name: string;
  spaceId: string | null;
  /**
   * False for someone with no organisation of their own (none, or a Guest elsewhere only): the claim names no
   * organisation and the server puts the device in their personal one, made on the first claim (UR-10, bug log A11).
   */
  ownOrg?: boolean;
};

/**
 * Claims a device in stock by its label (FR-DEV-01, P-61): reads the label (refusing a misread before any call),
 * gets a single-use nonce, computes the MAC under the label's local key in a worker, and posts the proof. The local
 * key itself never leaves the browser (VisionPlatform contracts/claim-proof.md).
 */
export async function claimDevice(input: ClaimInput): Promise<Device> {
  const label = parseLabel(input.label);
  const client = apiClient("customers");
  // An empty X-Org keeps the Guest organisation's out (the client adds the current one otherwise).
  const header = input.ownOrg === false ? { "X-Org": "" } : undefined;
  const { nonce } = await unwrap(client.GET("/v1/devices/claim/nonce", { params: { header } }));
  const mac = await computeClaimMac({
    kLocal: label.kLocal,
    deviceId: label.deviceIdBytes,
    lgen: label.lgen,
    nonce: base64ToBytes(nonce),
  });
  return unwrap(
    client.POST("/v1/devices/claim", {
      params: { header },
      body: {
        device_id: label.deviceId,
        lgen: label.lgen,
        nonce,
        mac: bytesToBase64(mac),
        name: input.name.trim(),
        space_id: input.spaceId,
      },
    }),
  );
}
